import {AspectRatio, Box, Flex, Image, Text, VStack} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import {useRef} from 'react'
import {ArrowLeftIcon, GiftIcon} from '_app/components/Icons'
import {Button} from '_app/components'
import theme from 'theme'
import {dropMaybe} from '_app/utils/dropMaybe'
import {ToggleEngraving} from './ToggleEngraving'
import {PriceRow} from './PriceRow'
import {EngravingGraphic} from './EngravingGraphic'
import ElementsSizesEnum from '../../../../enums/ElementsSizesEnum/ElementsSizesEnum'
import EventsHelper from '../../../../helpers/EventsHelper/EventsHelper'
import LocalStorageHelper from '../../../../helpers/LocalStorageHelper/LocalStorageHelper'

const CONTENT_WIDTH = '35.625rem'

export const EngravingBody = ({
  onDrawerClose,
  pendantsOptions,
  activePendantId,
  setActivePendantId,
  activeSide,
  setActiveSide,
  activePendantIdx,
  setActivePendantIdx,
  pendantSideTabs,
  activeTabId,
  getPreviewImage,
  isFree,
  ActiveTabComponent,
  activeOption,
  onAddOption,
  buttonProps,
  productName,
  productPrice,
  onRemoveOption,
  totalPrice,
  selectedEngraveOptionsArray,
  setActiveTabId,
  isSubmitting,
  engravingsData,
  isConfiguratorPage = false,
}: any) => {
  const { t } = useTranslation(['product', 'engraving', 'configurator'])
  const componentContainerRef = useRef<HTMLDivElement>(null)
  const image = getPreviewImage()
  const handleActiveOptionDelete = () => {
    if (!activeOption?.value) return
    return onRemoveOption(activeOption.value)
  }
  return (
    <>
      <Flex
        alignItems={isConfiguratorPage ? 'flex-start' : 'center'}
        gap={{ base: '1rem', md: 0 }}
        position={
          isConfiguratorPage ? 'static' : { base: 'fixed', md: 'relative' }
        }
        top={0}
        zIndex={9}
        py={{ base: 2, md: 0 }}
        width="100%"
        background="white"
        maxWidth="calc(100vw - 1.75rem)"
        height={isConfiguratorPage ? 'auto' : ElementsSizesEnum.ENGRAVING_NAVBAR_HEIGHT.base}
      >
        {!isConfiguratorPage && (
          <Box role="button" aria-label={t('CLOSE')} cursor="pointer">
            <ArrowLeftIcon onClick={onDrawerClose} />
          </Box>
        )}

        <Flex
          align={isConfiguratorPage ? 'flex-start' : 'center'}
          justify={isConfiguratorPage ? 'flex-start' : 'flex-end'}
          flex={1}
          overflowX={isConfiguratorPage ? 'visible' : { md: 'auto' }}
          flexDirection={isConfiguratorPage ? 'column' : 'row'}
          pl={isConfiguratorPage ? 0 : { base: 0, md: 28 }}
        >
          <Flex
            align="center"
            justify={{ base: 'flex-end', md: 'center' }}
            flex={1}
            display={{ base: 'flex', md: isConfiguratorPage ? 'flex' : 'none' }}
            my={isConfiguratorPage ? 4 : 0}
          >
            <ToggleEngraving
              {...{
                pendantsOptions,
                activePendantId,
                setActivePendantId,
                activeSide,
                setActiveSide,
                activePendantIdx,
                setActivePendantIdx,
              }}
            />
          </Flex>

          <Flex
            w={
              isConfiguratorPage
                ? '100%'
                : {
                    base: '100%',
                    md: CONTENT_WIDTH,
                  }
            }
            bg="#fff"
            justifyContent="flex-start"
            zIndex={9}
            top={{ base: ElementsSizesEnum.ENGRAVING_NAVBAR_HEIGHT.base, lg: 'unset' }}
            position={{ base: 'sticky', lg: 'relative' }}
            overflowX="auto"
            css={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
            px={{ base: 4, lg: 0 }}
            gap={{ base: 8, lg: 4 }}
            borderBottom="1px solid #e5e5e5"
            display={{ base: 'none', md: 'flex' }}
          >
            {pendantSideTabs?.map(({ name, id }) => (
              <Flex
                key={id}
                borderBottom={
                  id === activeTabId
                    ? '3px solid #fcca9d'
                    : '3px solid transparent'
                }
                onClick={() => {
                  setActiveTabId(id)
                  EventsHelper.scrollToTop()
                  componentContainerRef.current?.scrollTo({ top: 0 })
                }}
                height="3.625rem"
                alignItems="center"
                lineHeight={1.88}
                cursor="pointer"
                flexShrink={0}
                position="relative"
                justifyContent="flex-end"
              >
                {name}
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Flex
        flexDirection={{
          base: 'column',
          md: isConfiguratorPage ? 'column' : 'unset',
        }}
        gap={{ base: 'unset', md: isConfiguratorPage ? 0 : '1.5rem' }}
        alignItems={{ base: 'center', md: 'flex-start' }}
        height="calc(100% - 134px)"
      >
        <Flex
          w={{
            base: '100%',
            md: CONTENT_WIDTH,
          }}
          bg="#fff"
          justifyContent="flex-start"
          zIndex={9}
          top={{ base: ElementsSizesEnum.ENGRAVING_NAVBAR_HEIGHT.base, lg: 'unset' }}
          position={
            isConfiguratorPage ? 'static' : { base: 'absolute', lg: 'relative' }
          }
          overflowX="auto"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          px={{ base: 4, lg: 0 }}
          gap={{ base: 8, lg: 4 }}
          borderBottom="1px solid #e5e5e5"
          borderTop={{ base: '1px solid #e5e5e5', md: 'none' }}
          boxShadow={{ md: '0 10px 15px -10px #e5e5e5', base: 'none' }}
          display={{ base: 'flex', md: 'none' }}
          width="100%"
        >
          {pendantSideTabs?.map(({ name, id }) => (
            <Flex
              key={id}
              borderBottom={
                id === activeTabId
                  ? '3px solid #fcca9d'
                  : '3px solid transparent'
              }
              onClick={() => {
                setActiveTabId(id)
                EventsHelper.scrollToTop()
              }}
              height="3.625rem"
              alignItems="center"
              lineHeight={1.88}
              px={0.5}
              cursor="pointer"
              flexShrink={0}
              position="relative"
              justifyContent="flex-end"
            >
              {name}
            </Flex>
          ))}
        </Flex>

        <Box
          gap={4}
          display="flex"
          flexDirection="column"
          pt={{ base: isConfiguratorPage ? '2rem' : '8.375rem', md: 0 }}
        >
          <Flex
            align="center"
            justify={{ base: 'flex-end', md: 'center' }}
            flex={1}
            display={{ base: 'none', md: isConfiguratorPage ? 'none' : 'flex' }}
          >
            <ToggleEngraving
              {...{
                pendantsOptions,
                activePendantId,
                setActivePendantId,
                activeSide,
                setActiveSide,
                activePendantIdx,
                setActivePendantIdx,
              }}
            />
          </Flex>

          {image?.url && (
            <Box position="relative" mb={16} mt={{ base: 3, md: 0 }}>
              <EngravingGraphic
                selectedEngraveOptionsArray={selectedEngraveOptionsArray}
                activePendantId={activePendantId}
                activeSide={activeSide}
                sku={dropMaybe(activeOption?.value?.pendant)}
              />
              <AspectRatio
                ratio={1}
                minWidth={{ base: '21.5rem', md: '20.625rem' }}
                background={theme.colors.lightPink}
              >
                <Image src={image.url} alt={image.label || ''} />
              </AspectRatio>
            </Box>
          )}
        </Box>

        <Box
          width={
            isConfiguratorPage ? '100%' : { base: 'full', md: CONTENT_WIDTH }
          }
          height="calc(100% + 2.5rem)"
        >
          <Flex
            flexDirection="column"
            justifyContent={{ base: 'flex-start', md: 'space-between' }}
            height="100%"
          >
            <Box
              ref={componentContainerRef}
              mt={{ md: 6, base: 0 }}
              overflowY={{ base: 'unset', md: 'auto' }}
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {isFree && (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  border={{ base: 'none', md: 'solid 1px #e5e5e5' }}
                  borderBottom={{ base: 'solid 1px #e5e5e5' }}
                  p={{ base: '0.5rem 0', md: '0.875rem' }}
                  mt={{ base: 0, md: 7 }}
                  mb={8}
                >
                  <GiftIcon display={{ base: 'none', md: 'block' }} />
                  <Text fontWeight={300} pl={{ md: 2, base: 0 }}>
                    {t('YOU_WILL_RECEIVE_THIS_ENGRAVING_FOR_FREE', {
                      ns: 'engraving',
                    })}
                  </Text>
                </Flex>
              )}
              {!!ActiveTabComponent && activeOption && (
                <ActiveTabComponent
                  option={activeOption}
                  buttonProps={{
                    ...buttonProps,
                    children:
                      buttonProps.children ||
                      t('ADD_ENGRAVING', { ns: 'product' }),
                  }}
                  onAddOption={onAddOption}
                  onDeleteOption={handleActiveOptionDelete}
                  engravingsData={engravingsData}
                />
              )}
            </Box>

            <Box borderTopWidth={1} borderTopColor="divider">
              <Flex
                borderBottomWidth={1}
                borderBottomColor="divider"
                width="100%"
                mb={4}
                py={4}
                flexDirection="column"
                gap={2}
              >
                <PriceRow text={productName} amount={productPrice} />

                {selectedEngraveOptionsArray?.map(option => {
                  const GRAW_CODE_KEY = option.code?.toUpperCase() || ''
                  const SIDE_KEY = option.placement?.toUpperCase() || ''

                  const text = `${t(GRAW_CODE_KEY, {
                    ns: 'engraving',
                  })} ${t(SIDE_KEY)}`

                  return (
                    <PriceRow
                      key={option.uid + option.pendant_idx}
                      text={text}
                      amount={{
                        value: option.price,
                        currency: productPrice.currency,
                      }}
                      onDelete={() => onRemoveOption(option)}
                    />
                  )
                })}
                <PriceRow
                  text={t('SUMMARY', { ns: 'common' })}
                  amount={{
                    value: totalPrice,
                    currency: productPrice.currency,
                  }}
                />
              </Flex>
              <VStack mb={{ base: 4, md: 0 }}>
                <Button
                  width="full"
                  type="submit"
                  form="product-form"
                  isLoading={isSubmitting}
                  disabled={selectedEngraveOptionsArray?.length === 0 && LocalStorageHelper.get('engraving')}
                >
                  {isConfiguratorPage
                    ? t('SUMMARY', { ns: 'configurator' })
                    : t('ADD_TO_CART', { ns: 'common' })}
                </Button>
              </VStack>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </>
  )
}
